import { Box, IconButton, styled } from '@mui/material';
import { useMemo } from 'react';
import FileSyncButton from 'app/_model/sync/components/FileSyncButton';
import RemoveDocumentsButton from 'shared/_model/docs/components/RemoveDocumentsButton';
import { useProjectContext } from 'shared/_pages/project/provider';
import { IconDelete, IconSync } from 'shared/_ui/icon';
import DESIGN_COLORS from 'shared/_ui/design-colors';
import CloseIcon from '@mui/icons-material/Close';

const Toolbar = styled('div', { name: 'SelectedFilesToolbar' })(({ theme }) => ({
	position: 'sticky',
	top: 0,
	zIndex: theme.zIndex.appBar,
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(3.5),
	margin: theme.spacing(0, 0, 4),
	padding: theme.spacing(2.5, 4),
	borderRadius: theme.spacing(2.5),
	backgroundColor: DESIGN_COLORS.dark,
	color: '#ffffff',
}));

/** Translucent white button on the dark toolbar. */
const BUTTON_SX = {
	color: '#ffffff',
	backgroundColor: 'rgba(255, 255, 255, 0.1)',
	'&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.18)' },
	'&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.4)' },
} as const;

/** Delete turns into the theme error color on hover. */
const DELETE_BUTTON_SX = {
	...BUTTON_SX,
	'&:hover': { backgroundColor: 'error.main', color: 'error.contrastText' },
} as const;

/**
 * Dark bar with "N selected", Sync and Delete for the selected project files and a clear button.
 * Rendered only while at least one file is selected.
 */
export default function SelectedFilesToolbar() {
	const { files, project, removeFile, selectedFileIds, setSelectedFileIds } = useProjectContext();

	const selectedFiles = useMemo(
		() => files.filter((f) => selectedFileIds.includes(f._id)),
		[files, selectedFileIds]
	);

	if (!selectedFiles.length) {
		return null;
	}

	return (
		<Toolbar role="toolbar" aria-label="Bulk actions">
			<Box component="span" sx={{ whiteSpace: 'nowrap', fontSize: '0.84375rem', fontWeight: 600 }}>
				{selectedFiles.length} selected
			</Box>
			<Box
				sx={{
					display: 'flex',
					flexWrap: 'wrap',
					alignItems: 'center',
					justifyContent: 'flex-end',
					gap: 2,
					marginLeft: 'auto',
				}}
			>
				<FileSyncButton
					files={selectedFiles}
					disabled={!project.storeInCloud}
					size="small"
					startIcon={<IconSync fontSize="small" />}
					sx={BUTTON_SX}
				/>
				<RemoveDocumentsButton
					docs={selectedFiles}
					action={removeFile}
					size="small"
					startIcon={<IconDelete fontSize="small" />}
					sx={DELETE_BUTTON_SX}
				/>
				<IconButton
					size="small"
					aria-label="Clear selection"
					onClick={() => setSelectedFileIds(undefined, false)}
					sx={{ color: '#ffffff', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.18)' } }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>
		</Toolbar>
	);
}
